/**
 * ComplianceReviewDetail — one change-tracker item as a briefing (Bill 2026-11-04: "Everyone (Reid, Peter, Anthony,
 * Jessica, examiners) must instantly understand each item: what it is, why, what was done, what hasn't been done, why").
 *
 *   Header      the item number, its one-line summary, the compliance flag, where it stands, the app, the category
 *   Briefing    What it is · Why · Why it was flagged for compliance · What was done · What is not done yet
 *               (complianceModel.briefing: composed from the item's fields, history and links; nothing invented)
 *   Review      Confirm impact · Clear · Add note · Request undo (only once the change is live; asks to confirm).
 *               Every action needs a note. Shown only to a compliance reviewer; everyone else reads the item and is told
 *               who the reviewers are.
 *   History     every recorded step, newest first, who and when, with each note
 *
 * A review sends the version read with the page; if someone changed the item meanwhile, the server refuses (409) and
 * the page reloads it so the reviewer sees the current state before deciding.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { PageHeader, StatusChip } from '@retail-ops/panel-shell';
import {
  loadReviewItem,
  ReviewApiError,
  submitReview,
  type ReviewAction,
  type ReviewDetail,
  type ReviewViewer,
} from '@/services/complianceReviewsApi';
import {
  actionsFor,
  briefing,
  categoryWords,
  flagBadge,
  historyLines,
  NOTE_MAX,
  platformWords,
  statusWords,
  type ActionDef,
  type BriefLine,
} from './complianceModel';
import { ReviewerNotice } from './ComplianceReviewsQueue';
import { refreshReviewSummary } from './useReviewSummary';
import './complianceReviews.css';

function Lines({ lines }: { lines: ReadonlyArray<BriefLine> }): JSX.Element {
  return (
    <ul className="cr-brief__lines">
      {lines.map((l, i) => (
        <li key={i}>
          {l.url ? (
            <a href={l.url} target="_blank" rel="noopener noreferrer">
              {l.text}
            </a>
          ) : (
            l.text
          )}
          {l.detail && <div className="cr-brief__detail">{l.detail}</div>}
        </li>
      ))}
    </ul>
  );
}

function ReviewPanel({ item, onDone }: { item: ReviewDetail; onDone: (message: string, reload: boolean) => void }): JSX.Element {
  const [active, setActive] = useState<ActionDef | null>(null);
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const choose = (a: ActionDef): void => {
    setActive(a);
    setConfirming(false);
    setError(null);
  };

  const send = async (): Promise<void> => {
    if (!active) return;
    if (active.action === 'request-revert' && !confirming) {
      setConfirming(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await submitReview(item.key, active.action as ReviewAction, note.trim(), item.version);
      let msg = `${active.label} recorded.`;
      if (active.action === 'request-revert') {
        msg += r.revertAsk?.filed
          ? ` The developers were sent Innovation Hub request #${r.revertAsk.askId}.`
          : ' The undo is recorded, but the request to the developers could not be sent; choose Request undo again to resend it.';
      }
      setNote('');
      setActive(null);
      setConfirming(false);
      onDone(msg, true);
    } catch (e) {
      if (e instanceof ReviewApiError && e.status === 409 && e.code === 'CONCURRENT_UPDATE') {
        onDone('Someone changed this item while you were reading it. It has been reloaded: check it, then decide again.', true);
        return;
      }
      setError(e instanceof Error ? e.message : 'Your review was not saved.');
    } finally {
      setBusy(false);
    }
  };

  const actions = actionsFor(item);
  return (
    <section className="card cr-review" aria-labelledby="cr-review-h">
      <h2 id="cr-review-h" className="cr-h2">Your review</h2>
      <p className="form-hint">Choose what you decided. Every choice needs a note; it is recorded on the item with your name and the time.</p>
      <div className="cr-review__actions" role="group" aria-label="Review actions">
        {actions.map((a) => (
          <button
            key={a.action}
            type="button"
            className={`btn btn--sm ${active?.action === a.action ? (a.tone === 'danger' ? 'btn--danger' : 'btn--primary') : 'btn--ghost'} cr-review__btn cr-review__btn--${a.tone}`}
            aria-pressed={active?.action === a.action}
            onClick={() => choose(a)}
          >
            {a.label}
          </button>
        ))}
      </div>
      {active && (
        <div className="cr-review__form">
          <p className="cr-review__help">{active.help}</p>
          <label className="form-field">
            <span className="form-field__label">Note (required)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              maxLength={NOTE_MAX}
              placeholder={active.placeholder}
            />
          </label>
          {confirming && (
            <div className="banner banner--warn" role="alertdialog" aria-label="Confirm the undo request">
              <strong>Ask the developers to undo {item.key}?</strong> The change is live in the app. They will receive an
              Innovation Hub request with your note, and the item will show “Undo requested” until it is undone.
            </div>
          )}
          {error && (
            <div className="banner banner--err" role="alert">
              {error}
            </div>
          )}
          <div className="cr-review__submit">
            <button
              type="button"
              className={active.action === 'request-revert' ? 'btn btn--danger' : 'btn btn--primary'}
              disabled={busy || note.trim().length === 0}
              onClick={() => void send()}
            >
              {busy ? 'Saving…' : confirming ? 'Yes, request the undo' : active.label}
            </button>
            <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => { setActive(null); setConfirming(false); setError(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export function ComplianceReviewDetail(): JSX.Element {
  const { key = '' } = useParams();
  const [item, setItem] = useState<ReviewDetail | null>(null);
  const [viewer, setViewer] = useState<ReviewViewer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const r = await loadReviewItem(key);
      setItem(r.item);
      setViewer(r.viewer);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the item.');
    }
  }, [key]);

  useEffect(() => {
    setItem(null);
    setMessage(null);
    void load();
  }, [load]);

  const back = (
    <Link className="back-link" to="/compliance-reviews">
      ← Compliance reviews
    </Link>
  );

  if (error && !item) {
    return (
      <div className="cr-page">
        {back}
        <div className="banner banner--err" role="alert">
          {error}
        </div>
      </div>
    );
  }
  if (!item) {
    return (
      <div className="cr-page">
        {back}
        <p className="empty">Loading {key}…</p>
      </div>
    );
  }

  const flag = flagBadge(item.complianceFlag);
  const b = briefing(item);
  const history = historyLines(item.events);

  return (
    <div className="cr-page">
      {back}
      <PageHeader title={<><span className="cr-key">{item.key}</span> {item.oneLiner?.trim() || 'No one-line summary yet'}</>} />
      <div className="cr-facts">
        <StatusChip tone={flag.tone}>{flag.label}</StatusChip>
        <span className="cr-fact"><span className="cr-label">Where it stands:</span> {statusWords(item.status)}</span>
        <span className="cr-fact"><span className="cr-label">App:</span> {platformWords(item.platform)}</span>
        <span className="cr-fact"><span className="cr-label">Kind of change:</span> {categoryWords(item.category)}</span>
      </div>
      <p className="cr-flag-explain">{flag.explain}</p>

      {message && (
        <div className="banner banner--info" role="status">
          {message}
        </div>
      )}
      {error && (
        <div className="banner banner--err" role="alert">
          {error}
        </div>
      )}

      <section className="card cr-brief" aria-label="Briefing">
        <div className="cr-brief__block">
          <h2 className="cr-h2">What it is</h2>
          <Lines lines={b.whatItIs} />
        </div>
        <div className="cr-brief__block">
          <h2 className="cr-h2">Why</h2>
          <Lines lines={b.why} />
        </div>
        <div className="cr-brief__block cr-brief__block--flag">
          <h2 className="cr-h2">Why it was flagged for compliance</h2>
          <p>{b.whyFlagged ?? 'No reason was written when it was flagged.'}</p>
        </div>
        <div className="cr-brief__block">
          <h2 className="cr-h2">What was done</h2>
          <Lines lines={b.done} />
        </div>
        <div className="cr-brief__block">
          <h2 className="cr-h2">What is not done yet</h2>
          {b.notDone.length === 0 ? <p>Nothing: every step is complete.</p> : <Lines lines={b.notDone} />}
        </div>
      </section>

      {viewer?.canReview ? (
        <ReviewPanel
          item={item}
          onDone={(msg, reload) => {
            setMessage(msg);
            void refreshReviewSummary();
            if (reload) void load();
          }}
        />
      ) : (
        <ReviewerNotice />
      )}

      <section className="card" aria-labelledby="cr-history-h">
        <h2 id="cr-history-h" className="cr-h2">History</h2>
        {history.length === 0 ? (
          <p className="empty">No history recorded.</p>
        ) : (
          <ol className="cr-history">
            {history.map((h) => (
              <li key={h.id} className="cr-history__item">
                <div className="cr-history__what">{h.what}</div>
                <div className="cr-history__meta">
                  {h.who} · {h.when}
                  {h.viaPanel && ' · in the Risk panel'}
                </div>
                {h.note && <div className="cr-history__note">{h.note}</div>}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
